import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import { LookerAiComponents as LookerAiInstallSpec } from "@p0security/integrations-looker-ai-shared/components";
import type { LookerAiComponents } from "@p0security/integrations-looker-ai-shared/types";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type React from "react";

import {
  GCP_APPLY_ALL,
  type GcpSecretManagerConfig,
  type InstallContext,
  renderCreateAdminKeySecret,
  renderGcpTf,
  renderStoreAdminKey,
} from "./iac.ts";

type LookerAiItem = ItemConfigOf<LookerAiComponents["credential"]>;

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<LookerAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** The install's Terraform, in the order it has to be applied: the Admin
 * key's secret, then the key itself, then everything else. P0's install check
 * signs in to Looker with the key, so the key has to be in its secret before
 * the install finishes. */
const renderGcpInstructions = (
  context: InstallContext,
  id: string,
  item: LookerAiItem,
  secretManager: GcpSecretManagerConfig
): InstallCommands => ({
  commands: {
    iac: [
      {
        header:
          "Add this to your Terraform config. It deploys P0's Looker connector, creates the Admin key's secret, and lets the connector manage this instance's grant secrets.",
        command: renderGcpTf(context, id, item, secretManager),
      },
      {
        header: "1. Create the Admin key's secret:",
        command: renderCreateAdminKeySecret(id),
      },
      {
        header:
          "2. Have a Looker admin create a Looker service account with the Admin role, and an API key for it. Set LOOKER_CLIENT_ID and LOOKER_CLIENT_SECRET to the key's client ID and client secret, then add the key to the secret:",
        command: renderStoreAdminKey(item.adminKeySecret, secretManager),
      },
      {
        header: '3. Create everything else, then click "Next":',
        command: GCP_APPLY_ALL,
      },
    ],
  },
});

const { Paragraph } = Typography;

/** What the customer should know before agents get access. Shown at configure,
 * in place of the agent content folder's description, so it ends with that
 * description. */
const AgentAccessNotes: React.FC = () => (
  <div>
    <Paragraph type="secondary">
      Agents reach Looker through P0&apos;s gateway, which reads each
      grant&apos;s key from Google Secret Manager. Install GCP Workload Identity
      Federation in the secrets&apos; project, or every grant fails.
    </Paragraph>
    <Paragraph type="secondary">
      Each grant creates its own Looker service account, with a Standard seat,
      and deletes it when the grant ends. These accounts join your
      instance&apos;s default groups and hold those groups&apos; roles. So an
      edit grant can save content wherever the All Users group can, which
      includes the Shared folder by default.
    </Paragraph>
    <Paragraph type="secondary">
      {LookerAiInstallSpec.credential.schema.contentFolderId.description}
    </Paragraph>
  </div>
);

export const lookerAiInstaller: FrontendInstaller<LookerAiComponents> = {
  components: {
    credential: {
      // The AWS and Azure Key Vault options are hidden: the connector runs on
      // Cloud Run only.
      instructions: (context, id, item) =>
        item.secretManager.type === "gcp-sm"
          ? renderGcpInstructions(context, id, item, item.secretManager)
          : { commands: {} },
      items: {
        contentFolderId: {
          // Any other state keeps the field's own description.
          labeler: (_, _id, { state }) =>
            state === "configure" ? <AgentAccessNotes /> : undefined,
        },
        secretManager: {
          options: {
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
